import React, { useEffect, useState } from "react";
import { Form, Button, Card, Container, Row, Col, Badge, Spinner, Toast, ToastContainer, Table } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { Helmet } from "react-helmet";
import { useParams, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { auth, fires } from "../../firebase";
import { DataGrid } from "@mui/x-data-grid";
import firebase from "firebase";
import Papa from "papaparse";

export default function AdminEmailCampaign() {
  const maxW = isMobile ? "100%" : "90%";
  const location = useLocation();
  const { orgId: routeOrgId } = useParams();
  const qsOrgId = new URLSearchParams(location.search).get('orgId');
  const orgId = routeOrgId || qsOrgId || '';
  const [adminData, setAdminData] = useState({
      name: '',
      email: '',
      roles: []
  })
  const [userEmails, setUserEmails] = useState([])
  const [manualEmails, setManualEmails] = useState([]) // Manually added emails
  const [importedEmails, setImportedEmails] = useState([]) // Emails imported from CSV
  const [emailInput, setEmailInput] = useState('') // Input field for adding emails
  const [organizations, setOrganizations] = useState([])
  const [events, setEvents] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(orgId)
  const [selectedEventId, setSelectedEventId] = useState('')
  const [campaignType, setCampaignType] = useState('organization') // 'organization', 'event', or 'all-organizers'
  const [segmentIndex, setSegmentIndex] = useState(0)
  const [orgQuery, setOrgQuery] = useState('')
  const [eventQuery, setEventQuery] = useState('')
  const [selection, setSelection] = useState([])
  const [subject, setSubject] = useState('')
  const [templateId, setTemplateId] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [sendProgress, setSendProgress] = useState({ sent: 0, total: 0 })
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastVariant, setToastVariant] = useState('success')
  const [emailToken, setEmailToken] = useState('')
  const [pastCampaigns, setPastCampaigns] = useState([])
  const [loadingCampaigns, setLoadingCampaigns] = useState(false)
  async function loadUserEmails(){
    try {
      if (campaignType === 'organization' && selectedOrgId) {
        // Load by organization members: groups/{orgId}/members
        const membersSnap = await fires
          .collection('groups')
          .doc(selectedOrgId)
          .collection('members')
          .get();
        const seen = new Set();
        const rows = [];
        membersSnap.docs.forEach(doc => {
          const d = doc.data();
          const email = d.email;
          if (email && typeof email === 'string') {
            const emailLower = email.trim().toLowerCase();
            if (emailLower && !seen.has(emailLower)) {
              seen.add(emailLower);
              rows.push({ id: doc.id, email: email.trim() });
            }
          }
        });
        setUserEmails(rows);
        return;
      } else if (campaignType === 'event' && selectedEventId) {
        // Load by event members: events/{eventId}/members
        const membersSnap = await fires
          .collection('events')
          .doc(selectedEventId)
          .collection('members')
          .get();
        const seen = new Set();
        const rows = [];
        membersSnap.docs.forEach(doc => {
          const d = doc.data();
          const email = d.email;
          if (email && typeof email === 'string') {
            const emailLower = email.trim().toLowerCase();
            if (emailLower && !seen.has(emailLower)) {
              seen.add(emailLower);
              rows.push({ id: doc.id, email: email.trim() });
            }
          }
        });
        setUserEmails(rows);
        return;
      } else if (campaignType === 'all-organizers') {
        // Load all users from users collection where orgId exists
        // Note: Firestore doesn't support != null, so we fetch all and filter
        const usersSnap = await fires
          .collection('users')
          .get();
        const seen = new Set();
        const rows = [];
        usersSnap.docs.forEach(doc => {
          const d = doc.data();
          const email = d.email;
          const orgId = d.orgId;
          // Only include if email exists and orgId is not null/undefined/empty
          if (email && typeof email === 'string' && orgId && orgId !== '') {
            const emailLower = email.trim().toLowerCase();
            if (emailLower && !seen.has(emailLower)) {
              seen.add(emailLower);
              rows.push({ id: doc.id, email: email.trim() });
            }
          }
        });
        setUserEmails(rows);
        return;
      }
      // If no organization or event selected, clear recipients
      setUserEmails([]);
    } catch (err) {
      console.error('Failed to load emails:', err);
      setUserEmails([]);
    }
  }

  useEffect(() => {
      fires
      .collection('admin')
      .doc(auth.currentUser.uid)
      .get()
      .then((snapshot)=>{
          if (snapshot.exists){
            setAdminData({...snapshot.data()})
            // Load organizations and events for selection; preselect from URL if provided
            loadOrganizations()
            loadEvents()
          }
          else{
            window.location.href = "/"
          }
      })
  },[])

  const loadOrganizations = async () => {
    try {
      const snap = await fires.collection('groups').orderBy('timestamp', 'desc').get();
      const orgs = snap.docs.map(d => ({ id: d.id, name: d.data().name || d.data().groupName || d.id }));
      setOrganizations(orgs);
    } catch (e) {
      console.error('Failed to load organizations:', e);
      setOrganizations([]);
    }
  };

  const loadEvents = async () => {
    try {
      const snap = await fires.collection('events').orderBy('timestamp', 'desc').get();
      const evts = snap.docs.map(d => ({ 
        id: d.id, 
        name: d.data().name || d.data().title || d.data().eventName || d.id,
        orgId: d.data().orgId || d.data().organizationId || ''
      }));
      setEvents(evts);
    } catch (e) {
      console.error('Failed to load events:', e);
      setEvents([]);
    }
  };

  useEffect(() => {
    // When selectedOrgId or selectedEventId or campaignType changes, (re)load members
    setSelection([]);
    setSegmentIndex(0);
    loadUserEmails();
    // Generate a new session token per organization/event change
    setEmailToken(generateEmailToken());
    loadPastCampaigns();
  }, [selectedOrgId, selectedEventId, campaignType])

  const displayToast = (message, variant = 'success') => {
    setToastMessage(message)
    setToastVariant(variant)
    setShowToast(true)
  }

  // Combine loaded emails with manually added emails and imported emails
  // Ensure all emails are unique (case-insensitive)
  const SEGMENT_SIZE = 500
  const displayedRows = (() => {
    const start = segmentIndex * SEGMENT_SIZE
    const userEmailsSegment = userEmails.slice(start, start + SEGMENT_SIZE)
    
    // Combine all sources
    const allRows = [...userEmailsSegment, ...importedEmails, ...manualEmails]
    
    // Deduplicate by email (case-insensitive)
    const seen = new Set()
    const uniqueRows = []
    
    allRows.forEach(row => {
      if (row && row.email && typeof row.email === 'string') {
        const emailLower = row.email.trim().toLowerCase()
        if (emailLower && !seen.has(emailLower)) {
          seen.add(emailLower)
          uniqueRows.push({ ...row, email: row.email.trim() })
        }
      }
    })
    
    return uniqueRows
  })()

  // Ensure selected emails are unique (case-insensitive)
  const selectedEmails = (() => {
    const selected = displayedRows
      .filter(row => selection.includes(row.id))
      .map(row => row.email)
    
    // Deduplicate selected emails
    const seen = new Set()
    const unique = []
    selected.forEach(email => {
      if (email && typeof email === 'string') {
        const emailLower = email.trim().toLowerCase()
        if (emailLower && !seen.has(emailLower)) {
          seen.add(emailLower)
          unique.push(email.trim())
        }
      }
    })
    return unique
  })()

  function generateEmailToken() {
    try {
      const bytes = new Uint8Array(16);
      (window.crypto || window.msCrypto).getRandomValues(bytes);
      return Array.from(bytes)
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    } catch (e) {
      return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
  }
  const loadPastCampaigns = async () => {
    try {
      setLoadingCampaigns(true)
      setPastCampaigns([])
      if (campaignType === 'organization' && !selectedOrgId) return
      if (campaignType === 'event' && !selectedEventId) return
      if (campaignType === 'all-organizers') {
        // For all-organizers, we don't have a specific org/event to load campaigns from
        // You could load from a global campaigns collection or skip this
        setPastCampaigns([])
        setLoadingCampaigns(false)
        return
      }
      
      let snap;
      if (campaignType === 'organization') {
        snap = await fires
          .collection('groups')
          .doc(selectedOrgId)
          .collection('campaigns')
          .orderBy('timestamp', 'desc')
          .limit(20)
          .get()
      } else {
        snap = await fires
          .collection('events')
          .doc(selectedEventId)
          .collection('campaigns')
          .orderBy('timestamp', 'desc')
          .limit(20)
          .get()
      }
      
      const list = snap.docs.map(doc => {
        const d = doc.data() || {}
        const ts = d.timestamp?.toDate?.() ? d.timestamp.toDate() : (d.timestamp ? new Date(d.timestamp) : null)
        return {
          id: doc.id,
          subject: d.subject || d.title || '-',
          template: d.template || d.templateId || '-',
          status: d.status || 'sent',
          count: d.emails.length || d.count || d.total || 0,
          timestamp: ts
        }
      })
      setPastCampaigns(list)
    } catch (e) {
      console.error('Failed to load past campaigns:', e)
      setPastCampaigns([])
    } finally {
      setLoadingCampaigns(false)
    }
  }

  const handleCSVUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      displayToast('Please upload a valid CSV file', 'warning');
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          const emails = [];
          const seen = new Set();
          
          // Try to find email column - check common column names
          const emailColumnNames = ['email', 'Email', 'EMAIL', 'e-mail', 'E-mail', 'E-Mail', 'emailAddress', 'EmailAddress'];
          
          results.data.forEach((row, index) => {
            let email = null;
            
            // First, try to find a column named 'email' or similar
            for (const colName of emailColumnNames) {
              if (row[colName] && typeof row[colName] === 'string') {
                email = row[colName].trim();
                break;
              }
            }
            
            // If no email column found, try the first column that looks like an email
            if (!email) {
              for (const key in row) {
                const value = row[key];
                if (value && typeof value === 'string' && value.includes('@')) {
                  email = value.trim();
                  break;
                }
              }
            }
            
            // If still no email, try first column
            if (!email && Object.keys(row).length > 0) {
              const firstValue = row[Object.keys(row)[0]];
              if (firstValue && typeof firstValue === 'string') {
                email = firstValue.trim();
              }
            }
            
            // Validate email format and normalize
            if (email && typeof email === 'string' && email.includes('@')) {
              const emailTrimmed = email.trim();
              const emailLower = emailTrimmed.toLowerCase();
              if (emailLower && !seen.has(emailLower)) {
                seen.add(emailLower);
                emails.push({
                  id: `imported-${index}-${Date.now()}`,
                  email: emailTrimmed
                });
              }
            }
          });

          if (emails.length === 0) {
            displayToast('No valid emails found in CSV file', 'warning');
            return;
          }

          setImportedEmails(emails);
          displayToast(`Successfully imported ${emails.length} email(s) from CSV`, 'success');
          
          // Reset file input
          event.target.value = '';
        } catch (error) {
          console.error('Error parsing CSV:', error);
          displayToast('Error parsing CSV file. Please check the format.', 'danger');
        }
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        displayToast('Error reading CSV file. Please check the format.', 'danger');
      }
    });
  }

  const clearImportedEmails = () => {
    setImportedEmails([]);
    displayToast('Imported emails cleared', 'info');
  }

  const handleSend = async () => {
    if (selectedEmails.length === 0) {
      displayToast('Please select at least one recipient', 'warning')
      return
    }
    if (!subject || subject.trim() === '') {
      displayToast('Please enter a subject', 'warning')
      return
    }
    if (!templateId) {
      displayToast('Please select an email template', 'warning')
      return
    }
    // Ensure emails are unique and normalized before sending
    const uniqueEmailsToSend = (() => {
      const seen = new Set();
      const unique = [];
      selectedEmails.forEach(email => {
        if (email && typeof email === 'string') {
          const emailTrimmed = email.trim();
          const emailLower = emailTrimmed.toLowerCase();
          if (emailLower && !seen.has(emailLower)) {
            seen.add(emailLower);
            unique.push(emailTrimmed);
          }
        }
      });
      return unique;
    })();

    if (uniqueEmailsToSend.length === 0) {
      displayToast('No valid emails to send', 'warning');
      return;
    }

    setIsSending(true)
    setSendProgress({ sent: 0, total: uniqueEmailsToSend.length })

    try {
      let orgIdToSend = selectedOrgId;
      
      // If it's an event-based campaign, get the orgId from the event document
      if (campaignType === 'event' && selectedEventId) {
        const eventDoc = await fires.collection('events').doc(selectedEventId).get();
        if (eventDoc.exists) {
          const eventData = eventDoc.data();
          orgIdToSend = eventData.orgId;
        }
      }
      
      // If orgId is empty or not set, use 'event-go' as default
      if (!orgIdToSend || orgIdToSend === '') {
        orgIdToSend = 'event-go';
      }

      // Split emails into batches of 500
      const BATCH_SIZE = 500;
      const emailBatches = [];
      for (let i = 0; i < uniqueEmailsToSend.length; i += BATCH_SIZE) {
        emailBatches.push(uniqueEmailsToSend.slice(i, i + BATCH_SIZE));
      }

      const apiUrl = 'https://v2organizerapisendgroupemail-650318705789.us-central1.run.app'
      const token = await auth.currentUser.getIdToken()
      
      let totalSent = 0;
      let failedBatches = 0;
      const errors = [];

      // Send each batch sequentially
      for (let batchIndex = 0; batchIndex < emailBatches.length; batchIndex++) {
        const batch = emailBatches[batchIndex];
        const batchNumber = batchIndex + 1;
        
        try {
          displayToast(`Sending batch ${batchNumber} of ${emailBatches.length} (${batch.length} emails)...`, 'info')
          
          const res = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              emails: batch,
              template: templateId,
              subject: subject,
              orgId: orgIdToSend,
              eventId: selectedEventId,
              campaignType: campaignType,
              token: emailToken,
              sender: "Email Reminder",
            })
          })
          
          const data = await res.json()
          if (!res.ok) {
            throw new Error(data?.message || `Failed to send batch ${batchNumber}`)
          }
          
          totalSent += batch.length;
          setSendProgress({ sent: totalSent, total: uniqueEmailsToSend.length })
          
        } catch (err) {
          console.error(`Campaign send error for batch ${batchNumber}:`, err)
          failedBatches++;
          errors.push(`Batch ${batchNumber}: ${err.message || 'Failed to send'}`)
          // Continue with next batch even if one fails
        }
      }

      // Show final result
      if (failedBatches === 0) {
        displayToast(`Successfully sent ${totalSent} unique email(s) in ${emailBatches.length} batch(es)`, 'success')
      } else if (totalSent > 0) {
        displayToast(`Sent ${totalSent} email(s) successfully. ${failedBatches} batch(es) failed.`, 'warning')
        console.error('Failed batches:', errors)
      } else {
        throw new Error(`All batches failed. Errors: ${errors.join('; ')}`)
      }
    } catch (err) {
      console.error('Campaign send error:', err)
      displayToast(err.message || 'Failed to send emails', 'danger')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>View Email Campaign</title>
        <meta property="og:title" content="View Admins" />
        <meta
          property="og:description"
          content="View Admins"
        />
        <meta property="og:url" content={"https://evtgo.com/"} />
        <meta
          property="og:site_name"
          content="View Admins"
        />
        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
      </Helmet>
      <Container style={{ minHeight: '100vh', backgroundColor: 'white' }} className="py-4">
        <div style={{ width: maxW, margin: '0 auto' }}>
          <Card className="border-0" style={{ backgroundColor: 'transparent' }}>
            <Card.Body>
              <div className="mb-4">
                <Card.Title className="text-center" style={{ fontSize: '25px', fontWeight: 'bold' }}>
                  Email Campaign
                </Card.Title>
                <p className="text-center text-muted mb-0">Select recipients, choose a template, and send</p>
              </div>

              <Row className="g-3">
                <Col lg={4}>
                  <Card className="mb-3">
                    <Card.Header>
                      <h6 className="mb-0">Compose</h6>
                    </Card.Header>
                    <Card.Body>
                  {/* Segment Controls */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="small text-muted">
                      Max 500 recipients per send. Use segments to reach all members.
                    </div>
                  </div>

                  <Form.Group className="mb-3">
                    <Form.Label>Campaign Type</Form.Label>
                    <Form.Select
                      value={campaignType}
                      onChange={(e) => {
                        setCampaignType(e.target.value);
                        setSelectedOrgId('');
                        setSelectedEventId('');
                        setOrgQuery('');
                        setEventQuery('');
                      }}
                    >
                      <option value="organization">Organization-based</option>
                      <option value="event">Event-based</option>
                      <option value="all-organizers">All Organizers</option>
                    </Form.Select>
                    <Form.Text className="text-muted">Choose whether to send to organization members, event members, or all organizers</Form.Text>
                  </Form.Group>

                  {campaignType === 'organization' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Organization</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by organization name or ID"
                        value={orgQuery}
                        onChange={(e) => setOrgQuery(e.target.value)}
                        className="mb-2"
                      />
                      <Form.Select
                        value={selectedOrgId}
                        onChange={(e) => setSelectedOrgId(e.target.value)}
                      >
                        <option value="">Select an organization...</option>
                        {organizations
                          .filter(org => {
                            if (!orgQuery) return true;
                            const q = orgQuery.toLowerCase();
                            return (org.name || '').toLowerCase().includes(q) || (org.id || '').toLowerCase().includes(q);
                          })
                          .slice(0, 100)
                          .map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </Form.Select>
                      <div className="d-flex justify-content-between">
                        <Form.Text className="text-muted">Choose an organization to load its members</Form.Text>
                        {orgQuery && (
                          <small className="text-muted">Showing up to 100 matches</small>
                        )}
                      </div>
                    </Form.Group>
                  )}

                  {campaignType === 'event' && (
                    <Form.Group className="mb-3">
                      <Form.Label>Event</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search by event name or ID"
                        value={eventQuery}
                        onChange={(e) => setEventQuery(e.target.value)}
                        className="mb-2"
                      />
                      <Form.Select
                        value={selectedEventId}
                        onChange={(e) => setSelectedEventId(e.target.value)}
                      >
                        <option value="">Select an event...</option>
                        {events
                          .filter(event => {
                            if (!eventQuery) return true;
                            const q = eventQuery.toLowerCase();
                            return (event.name || '').toLowerCase().includes(q) || (event.id || '').toLowerCase().includes(q);
                          })
                          .slice(0, 100)
                          .map(event => (
                          <option key={event.id} value={event.id}>{event.name}</option>
                        ))}
                      </Form.Select>
                      <div className="d-flex justify-content-between">
                        <Form.Text className="text-muted">Choose an event to load its members</Form.Text>
                        {eventQuery && (
                          <small className="text-muted">Showing up to 100 matches</small>
                        )}
                      </div>
                    </Form.Group>
                  )}

                  {/* CSV Upload Section */}
                  <Form.Group className="mb-3">
                    <Form.Label>Import Emails from CSV</Form.Label>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        style={{ flex: 1 }}
                      />
                      {importedEmails.length > 0 && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={clearImportedEmails}
                        >
                          Clear ({importedEmails.length})
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      Upload a CSV file with email addresses. The CSV should have an "email" column or emails in the first column.
                    </Form.Text>
                  </Form.Group>

                  {/* Segment selector (only shown when more than 500) */}
                  {userEmails.length > 0 && (
                    <Form.Group className="mb-3">
                      <Form.Label>Segment</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        {(() => {
                          const SEGMENT_SIZE = 500;
                          const totalSegments = Math.max(1, Math.ceil(userEmails.length / SEGMENT_SIZE));
                          return (
                            <>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setSegmentIndex(Math.max(0, segmentIndex - 1))}
                                disabled={segmentIndex === 0}
                              >
                                Prev
                              </Button>
                              <Form.Select
                                value={segmentIndex}
                                onChange={(e) => setSegmentIndex(parseInt(e.target.value) || 0)}
                                style={{ maxWidth: '200px' }}
                              >
                                {Array.from({ length: totalSegments }).map((_, idx) => (
                                  <option key={idx} value={idx}>
                                    Segment {idx + 1} of {totalSegments}
                                  </option>
                                ))}
                              </Form.Select>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setSegmentIndex(Math.min(totalSegments - 1, segmentIndex + 1))}
                                disabled={segmentIndex >= totalSegments - 1}
                              >
                                Next
                              </Button>
                            </>
                          );
                        })()}
                      </div>
                      <Form.Text className="text-muted">
                        Only the current segment's recipients are shown and can be selected (max 500).
                      </Form.Text>
                    </Form.Group>
                  )}

                      <Form.Group className="mb-3">
                        <Form.Label>Subject</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter email subject"
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                        />
                      </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email Template ID</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter template ID (from Email Template Creator)"
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                    />
                    <Form.Text className="text-muted">Paste the template ID to use</Form.Text>
                  </Form.Group>

                      
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Tips</h6>
                    </Card.Header>
                    <Card.Body>
                      <ul className="mb-0 small">
                        <li>Use Email Template Creator to design content and copy its Template ID.</li>
                        <li>Ensure recipients have valid emails.</li>
                      </ul>
                    </Card.Body>
                  </Card>
                  <Card className="mt-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">Past Campaigns</h6>
                      <Button variant="outline-secondary" size="sm" onClick={loadPastCampaigns} disabled={((campaignType === 'organization' && !selectedOrgId) || (campaignType === 'event' && !selectedEventId) || campaignType === 'all-organizers') || loadingCampaigns}>
                        {loadingCampaigns ? 'Loading...' : 'Refresh'}
                      </Button>
                    </Card.Header>
                    <Card.Body>
                      {campaignType === 'all-organizers' ? (
                        <div className="text-muted small">Past campaigns are not available for all-organizers campaigns.</div>
                      ) : ((campaignType === 'organization' && !selectedOrgId) || (campaignType === 'event' && !selectedEventId)) ? (
                        <div className="text-muted small">Select a {campaignType} to view past campaigns.</div>
                      ) : pastCampaigns.length === 0 ? (
                        <div className="text-muted small">No campaigns found.</div>
                      ) : (
                        <Table size="sm" hover responsive className="mb-0">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Subject</th>
                              <th>Template</th>
                              <th className="text-center">Sent</th>
                              <th className="text-center">Status</th>
                              <th className="text-end">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pastCampaigns.map((c) => (
                              <tr key={c.id}>
                                <td><code className="text-muted">{c.id}</code></td>
                                <td className="text-truncate" style={{maxWidth:'200px'}}>{c.subject}</td>
                                <td><code>{c.template}</code></td>
                                <td className="text-center">{c.count}</td>
                                <td className="text-center">
                                  <Badge bg={c.status === 'pending' ? 'warning' : c.status === 'failed' ? 'danger' : 'success'}>
                                    {c.status}
                                  </Badge>
                                </td>
                                <td className="text-end small">{c.timestamp ? c.timestamp.toLocaleString() : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>

                  {/* Send button moved down */}
                  <div className="d-flex align-items-center justify-content-between mt-3">
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge bg="primary">Selected: {selectedEmails.length}</Badge>
                      <Badge bg="secondary">Segment Size: {displayedRows.length}</Badge>
                      <Badge bg="info">All Members: {userEmails.length}</Badge>
                      {importedEmails.length > 0 && (
                        <Badge bg="success">Imported: {importedEmails.length}</Badge>
                      )}
                    </div>
                    
                  </div>
                  {isSending && (
                    <div className="mt-2 small text-muted">
                      Progress: {sendProgress.sent} / {sendProgress.total}
                    </div>
                  )}
                  <br/>
                  <Button
                      variant="primary"
                      onClick={handleSend}
                      disabled={isSending || selectedEmails.length === 0 || !subject || !templateId}
                    >
                      {isSending ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Sending...
                        </>
                      ) : (
                        <>Send Campaign</>
                      )}
                    </Button>
                </Col>
                <Col lg={8}>
                  <Card>
                    <Card.Header>
                      <h6 className="mb-0">Recipients</h6>
                    </Card.Header>
                    <Card.Body>
                      <div style={{ height: 520, width: '100%' }}>
                        <DataGrid
                          checkboxSelection
                          disableRowSelectionOnClick
                          rows={displayedRows}
                          columns={[
                            { field: 'id', headerName: 'User ID', flex: 0.6 },
                            { field: 'email', headerName: 'Email', flex: 1.4 },
                          ]}
                          onRowSelectionModelChange={(newSelection) => {
                            setSelection(newSelection)
                          }}
                          rowSelectionModel={selection}
                        />
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>

        <ToastContainer position="top-end" className="p-3">
          <Toast show={showToast} onClose={() => setShowToast(false)} delay={5000} autohide bg={toastVariant === 'danger' ? 'danger' : undefined}>
            <Toast.Header closeButton>
              <strong className="me-auto">Email Campaign</strong>
            </Toast.Header>
            <Toast.Body className={toastVariant === 'danger' ? 'text-white' : ''}>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>
    </>
  );
}

